/**
 * Fleet vehicle overlay — add or edit one of the shop's own vehicles.
 *
 * Explicit save rather than the autosave used on customer vehicles, because a
 * fleet vehicle is usually entered once in a sitting and half a vehicle on the
 * schedule is worse than none.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { History, Plus, Trash2 } from 'lucide-react';

import { dataApi } from '../../services/api';
import {
  fleetVehicleGet,
  fleetVehicleCreate,
  fleetVehicleUpdate,
  fleetVehicleDelete,
  fleetEventList,
  fleetRecordCompliance,
  fleetBookingsForVehicle
} from '../../services/fleet';
import {
  BODY_TYPES,
  FUEL_TYPES,
  TRANSMISSIONS,
  VEHICLE_CATEGORIES,
  VEHICLE_SIZES,
  VEHICLE_STATUSES,
  complianceItems,
  complianceSummary,
  formatCurrency
} from '../../lib/fleet';
import { COMPLIANCE_TONE_STYLES } from '../../components/fleet/fleetStyles';
import { useObfuscate } from '../../hooks/useObfuscate';
import { cn } from '../../lib/utils';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import DeleteConfirmDialog from '../../components/dialogs/DeleteConfirmDialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DatePicker } from '../../components/ui/date-picker';
import { NumberInput } from '../../components/ui/number-input';
import { CurrencyInput } from '../../components/ui/currency-input';
import RecordHistoryButton from '../../components/history/RecordHistoryButton';

const EMPTY = {
  registration: '',
  displayName: '',
  vin: '',
  make: '',
  model: '',
  otherMake: '',
  otherModel: '',
  year: '',
  colour: '',
  category: 'loan',
  status: 'active',
  availableForLoan: true,
  notes: '',
  odometer: '',
  wofExpiryDate: '',
  regoExpiryDate: '',
  rucExpiryOdometer: '',
  serviceDueDate: '',
  serviceDueOdometer: '',
  remindersEnabled: true,
  transmission: '',
  fuelType: '',
  bodyType: '',
  vehicleSize: '',
  seats: '',
  doors: '',
  hasTowbar: false,
  childSeatAnchors: '',
  dailyRate: '',
  adminFee: ''
};

const dateValue = (iso) => (iso ? String(iso).split('T')[0] : '');
const numberOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

const COMPLIANCE_EVENT_TYPES = [
  { value: 'wof', label: 'WoF/CoF issued' },
  { value: 'rego', label: 'Rego renewed' },
  { value: 'ruc', label: 'RUC purchased' },
  { value: 'service', label: 'Service completed' },
  { value: 'repair', label: 'Repair' },
  { value: 'damage', label: 'Damage' },
  { value: 'note', label: 'Note' }
];

const FleetVehicleForm = ({ vehicleId, isOpen, onClose, onSaved, onDeleted }) => {
  const priv = useObfuscate();
  const isEdit = Boolean(vehicleId);

  const [form, setForm] = useState(EMPTY);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [makesModels, setMakesModels] = useState({});
  const [events, setEvents] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('details');

  const [eventDraft, setEventDraft] = useState({
    type: 'wof',
    occurredAt: format(new Date(), 'yyyy-MM-dd'),
    expiresAt: '',
    expiryOdometer: '',
    odometer: '',
    cost: '',
    supplier: '',
    description: ''
  });

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const load = useCallback(async () => {
    if (!vehicleId) {
      setForm(EMPTY);
      setVehicle(null);
      setEvents([]);
      setHistory([]);
      return;
    }
    setLoading(true);
    try {
      const [row, eventRows, bookingRows] = await Promise.all([
        fleetVehicleGet(vehicleId),
        fleetEventList(vehicleId),
        fleetBookingsForVehicle(vehicleId)
      ]);
      setVehicle(row);
      setEvents(eventRows);
      setHistory(bookingRows);
      setForm({
        ...EMPTY,
        ...Object.fromEntries(Object.keys(EMPTY).map((key) => [key, row[key] ?? EMPTY[key]])),
        wofExpiryDate: dateValue(row.wofExpiryDate),
        regoExpiryDate: dateValue(row.regoExpiryDate),
        serviceDueDate: dateValue(row.serviceDueDate)
      });
    } catch (err) {
      console.error('Failed to load fleet vehicle:', err);
      toast.error('Could not load this vehicle');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    if (!isOpen) return;
    setTab('details');
    load();
    dataApi
      .makesModelsList()
      .then(setMakesModels)
      .catch(() => setMakesModels({}));
  }, [isOpen, load]);

  const makeOptions = useMemo(() => {
    const makes = Object.keys(makesModels || {}).filter((k) => k !== '_records').sort();
    return makes.includes('Other') ? ['Other', ...makes.filter((m) => m !== 'Other')] : makes;
  }, [makesModels]);

  const modelOptions = useMemo(() => {
    if (!form.make) return [];
    const models = makesModels?.[form.make] || [];
    return models.includes('Other') ? ['Other', ...models.filter((m) => m !== 'Other')] : models;
  }, [makesModels, form.make]);

  const previewVehicle = useMemo(
    () => ({
      ...form,
      odometer: numberOrNull(form.odometer),
      rucExpiryOdometer: numberOrNull(form.rucExpiryOdometer),
      serviceDueOdometer: numberOrNull(form.serviceDueOdometer)
    }),
    [form]
  );

  /**
   * Body type and size come from the make/model reference data rather than
   * being typed in — the same lookup the customer vehicle form uses, so a
   * Corolla classifies identically whoever entered it. Still editable
   * afterwards for the odd variant the table doesn't distinguish.
   */
  const applyModelClassification = useCallback(
    (make, model) => {
      if (!make || !model || make === 'Other' || model === 'Other') return;
      const match = (makesModels?._records || []).find((r) => r.make === make && r.model === model);
      if (!match) return;
      setForm((prev) => ({
        ...prev,
        bodyType: match.bodyType || prev.bodyType,
        vehicleSize: match.vehicleSize || prev.vehicleSize
      }));
    },
    [makesModels]
  );

  const handleSave = async () => {
    if (!form.registration.trim()) {
      toast.error('A registration is required');
      setTab('details');
      return;
    }
    if (!form.make || !form.model) {
      toast.error('Make and model are required');
      setTab('details');
      return;
    }

    const payload = {
      registration: form.registration.trim().toUpperCase(),
      displayName: form.displayName.trim() || null,
      vin: form.vin.trim() || null,
      make: form.make,
      model: form.model,
      otherMake: form.make === 'Other' ? form.otherMake || null : null,
      otherModel: form.model === 'Other' ? form.otherModel || null : null,
      year: numberOrNull(form.year),
      colour: form.colour || null,
      category: form.category,
      status: form.status,
      availableForLoan: Boolean(form.availableForLoan),
      notes: form.notes || null,
      odometer: numberOrNull(form.odometer),
      wofExpiryDate: form.wofExpiryDate ? new Date(form.wofExpiryDate).toISOString() : null,
      regoExpiryDate: form.regoExpiryDate ? new Date(form.regoExpiryDate).toISOString() : null,
      rucExpiryOdometer: numberOrNull(form.rucExpiryOdometer),
      serviceDueDate: form.serviceDueDate ? new Date(form.serviceDueDate).toISOString() : null,
      serviceDueOdometer: numberOrNull(form.serviceDueOdometer),
      remindersEnabled: Boolean(form.remindersEnabled),
      transmission: form.transmission || null,
      fuelType: form.fuelType || null,
      bodyType: form.bodyType || null,
      vehicleSize: form.vehicleSize || null,
      seats: numberOrNull(form.seats),
      doors: numberOrNull(form.doors),
      hasTowbar: Boolean(form.hasTowbar),
      childSeatAnchors: numberOrNull(form.childSeatAnchors) ?? 0,
      dailyRate: numberOrNull(form.dailyRate),
      adminFee: numberOrNull(form.adminFee)
    };

    setSaving(true);
    try {
      const saved = isEdit ? await fleetVehicleUpdate(vehicleId, payload) : await fleetVehicleCreate(payload);
      toast.success(isEdit ? 'Vehicle updated' : 'Vehicle added to the fleet');
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      console.error('Failed to save fleet vehicle:', err);
      toast.error(err?.message || 'Could not save this vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (history.length > 0) {
      toast.error('This vehicle has bookings against it. Retire it instead so its history is kept.');
      return;
    }
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await fleetVehicleDelete(vehicleId);
      toast.success('Vehicle removed');
      onDeleted?.(vehicleId);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Could not remove this vehicle');
    }
  };

  const handleLogEvent = async () => {
    if (!vehicle) return;
    try {
      await fleetRecordCompliance(vehicle, {
        type: eventDraft.type,
        occurredAt: eventDraft.occurredAt ? new Date(eventDraft.occurredAt).toISOString() : new Date().toISOString(),
        expiresAt: eventDraft.expiresAt ? new Date(eventDraft.expiresAt).toISOString() : null,
        expiryOdometer: numberOrNull(eventDraft.expiryOdometer),
        odometer: numberOrNull(eventDraft.odometer),
        cost: numberOrNull(eventDraft.cost),
        supplier: eventDraft.supplier || null,
        description: eventDraft.description || null
      });
      toast.success('Logged — the vehicle’s expiry dates have been rolled forward');
      setEventDraft((prev) => ({ ...prev, expiresAt: '', expiryOdometer: '', cost: '', supplier: '', description: '' }));
      await load();
    } catch (err) {
      console.error('Failed to log event:', err);
      toast.error(err?.message || 'Could not log this');
    }
  };

  const totalRunningCost = useMemo(
    () => events.reduce((sum, e) => sum + (Number(e.cost) || 0), 0),
    [events]
  );

  const field = (label, node, hint) => (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {loading ? <Skeleton className="h-9 w-full" /> : node}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );

  const selectField = (label, value, options, onChange, placeholder) =>
    field(
      label,
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[92vh] w-250 max-w-[95vw] flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle>{isEdit ? 'Vehicle' : 'Add a fleet vehicle'}</DialogTitle>
            {isEdit && <RecordHistoryButton rowId={vehicleId} title="Fleet vehicle" />}
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
          <TabsList className="w-fit">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="compliance">Compliance</TabsTrigger>
            <TabsTrigger value="matching">Specification</TabsTrigger>
            <TabsTrigger value="charges">Charges</TabsTrigger>
            {isEdit && <TabsTrigger value="history">History</TabsTrigger>}
          </TabsList>

          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            <TabsContent value="details" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Identity</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {field(
                    'Registration *',
                    <Input
                      value={form.registration}
                      onChange={(e) => set('registration', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                      placeholder="ABC123"
                      className="font-mono uppercase"
                    />
                  )}
                  {field(
                    'Display name',
                    <Input
                      value={form.displayName}
                      onChange={(e) => set('displayName', e.target.value)}
                      placeholder="Silver Insight"
                    />,
                    'What staff call it on the schedule'
                  )}
                  {field(
                    'VIN',
                    <Input
                      value={form.vin}
                      onChange={(e) => set('vin', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 17))}
                      className="font-mono uppercase"
                    />
                  )}

                  {selectField('Make *', form.make, makeOptions.map((m) => ({ value: m, label: m })), (v) => {
                    set('make', v);
                    set('model', '');
                  }, 'Select make')}

                  {form.make === 'Other'
                    ? field('Specify make', <Input value={form.otherMake} onChange={(e) => set('otherMake', e.target.value)} />)
                    : selectField(
                        'Model *',
                        form.model,
                        modelOptions.map((m) => ({ value: m, label: m })),
                        (v) => {
                          set('model', v);
                          applyModelClassification(form.make, v);
                        },
                        'Select model'
                      )}

                  {form.make === 'Other' || form.model === 'Other'
                    ? field('Specify model', <Input value={form.otherModel} onChange={(e) => set('otherModel', e.target.value)} />)
                    : field('Year', <NumberInput max={new Date().getFullYear() + 1} value={form.year} onChange={(v) => set('year', v)} />)}

                  {(form.make === 'Other' || form.model === 'Other') &&
                    field('Year', <NumberInput max={new Date().getFullYear() + 1} value={form.year} onChange={(v) => set('year', v)} />)}

                  {field('Colour', <Input value={form.colour} onChange={(e) => set('colour', e.target.value)} />)}
                  {field(
                    'Odometer',
                    <NumberInput suffix="km" value={form.odometer} onChange={(v) => set('odometer', v)} />,
                    vehicle?.odometerUpdatedAt ? `Last read ${format(new Date(vehicle.odometerUpdatedAt), 'd MMM yyyy')}` : null
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Role</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {selectField('Category', form.category, VEHICLE_CATEGORIES, (v) => set('category', v), 'Select category')}
                  {selectField('Status', form.status, VEHICLE_STATUSES, (v) => set('status', v), 'Select status')}

                  <div className="flex items-center justify-between rounded-md border p-3 sm:col-span-2">
                    <div>
                      <Label className="text-sm">Available to lend</Label>
                      <p className="text-[11px] text-muted-foreground">
                        Turn off to keep the vehicle on the schedule but out of the availability search
                      </p>
                    </div>
                    <Switch checked={Boolean(form.availableForLoan)} onCheckedChange={(v) => set('availableForLoan', v)} />
                  </div>

                  <div className="sm:col-span-2">
                    {field('Notes', <Textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />)}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="compliance" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Current status</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {complianceItems(previewVehicle).map((item) => (
                    <span
                      key={item.key}
                      className={cn('rounded-md border px-2 py-1 text-xs font-medium', COMPLIANCE_TONE_STYLES[item.status])}
                    >
                      {item.label}: {complianceSummary(item)}
                    </span>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Expiries</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {field('WoF/CoF expiry', <DatePicker value={form.wofExpiryDate} onChange={(v) => set('wofExpiryDate', v)} placeholder="Not set" monthYearNav />)}
                  {field('Rego expiry', <DatePicker value={form.regoExpiryDate} onChange={(v) => set('regoExpiryDate', v)} placeholder="Not set" monthYearNav />)}
                  {field(
                    'RUC valid to',
                    <NumberInput suffix="km" value={form.rucExpiryOdometer} onChange={(v) => set('rucExpiryOdometer', v)} />,
                    'Diesel and EV road user charges'
                  )}
                  {field('Service due', <DatePicker value={form.serviceDueDate} onChange={(v) => set('serviceDueDate', v)} placeholder="Not set" monthYearNav />)}
                  {field(
                    'Service due at',
                    <NumberInput suffix="km" value={form.serviceDueOdometer} onChange={(v) => set('serviceDueOdometer', v)} />,
                    'Whichever comes first is what falls due'
                  )}

                  <div className="flex items-center justify-between rounded-md border p-3 sm:col-span-2">
                    <div>
                      <Label className="text-sm">Reminders</Label>
                      <p className="text-[11px] text-muted-foreground">Include this vehicle in expiry alerts</p>
                    </div>
                    <Switch checked={Boolean(form.remindersEnabled)} onCheckedChange={(v) => set('remindersEnabled', v)} />
                  </div>
                </CardContent>
              </Card>

              {isEdit && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Log a completed job</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-xs text-muted-foreground">
                      Records it against the vehicle and rolls the matching expiry forward in one step.
                    </p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                      {selectField('Type', eventDraft.type, COMPLIANCE_EVENT_TYPES, (v) => setEventDraft((p) => ({ ...p, type: v })), 'Type')}
                      {field('Date', <DatePicker value={eventDraft.occurredAt} onChange={(v) => setEventDraft((p) => ({ ...p, occurredAt: v }))} />)}
                      {['wof', 'rego', 'service'].includes(eventDraft.type) &&
                        field('New expiry', <DatePicker value={eventDraft.expiresAt} onChange={(v) => setEventDraft((p) => ({ ...p, expiresAt: v }))} placeholder="Not set" />)}
                      {['ruc', 'service'].includes(eventDraft.type) &&
                        field('Valid to', <NumberInput suffix="km" value={eventDraft.expiryOdometer} onChange={(v) => setEventDraft((p) => ({ ...p, expiryOdometer: v }))} />)}
                      {field('Odometer', <NumberInput suffix="km" value={eventDraft.odometer} onChange={(v) => setEventDraft((p) => ({ ...p, odometer: v }))} />)}
                      {field('Cost', <CurrencyInput value={numberOrNull(eventDraft.cost) ?? 0} onChange={(v) => setEventDraft((p) => ({ ...p, cost: v }))} />)}
                      {field('Supplier', <Input value={eventDraft.supplier} onChange={(e) => setEventDraft((p) => ({ ...p, supplier: e.target.value }))} />)}
                      <div className="sm:col-span-4">
                        {field('Notes', <Input value={eventDraft.description} onChange={(e) => setEventDraft((p) => ({ ...p, description: e.target.value }))} />)}
                      </div>
                    </div>
                    <Button size="sm" variant="secondary" onClick={handleLogEvent}>
                      <Plus className="mr-1 h-4 w-4" /> Log it
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="matching" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Specification</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    What the availability search matches on when finding a customer a suitable vehicle.
                    Body type and size are filled in from the model — change them if this one differs.
                  </p>
                </CardHeader>
                <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {selectField('Transmission', form.transmission, TRANSMISSIONS, (v) => set('transmission', v), 'Select')}
                  {selectField('Fuel', form.fuelType, FUEL_TYPES, (v) => set('fuelType', v), 'Select')}
                  {selectField('Body type', form.bodyType, BODY_TYPES, (v) => set('bodyType', v), 'Select')}
                  {selectField('Size class', form.vehicleSize, VEHICLE_SIZES, (v) => set('vehicleSize', v), 'Select')}
                  {field('Seats', <NumberInput max={20} value={form.seats} onChange={(v) => set('seats', v)} />)}
                  {field('Doors', <NumberInput max={6} value={form.doors} onChange={(v) => set('doors', v)} />)}
                  {field('Child seat anchors', <NumberInput max={6} value={form.childSeatAnchors} onChange={(v) => set('childSeatAnchors', v)} />)}

                  <div className="flex items-center gap-2 pt-6">
                    <Checkbox id="hasTowbar" checked={Boolean(form.hasTowbar)} onCheckedChange={(v) => set('hasTowbar', v)} />
                    <Label htmlFor="hasTowbar" className="text-sm">Towbar</Label>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="charges" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Default charges</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {field(
                    'Day rate',
                    <CurrencyInput value={numberOrNull(form.dailyRate) ?? 0} onChange={(v) => set('dailyRate', v)} />,
                    'Charged per 24 hours, any part of a day counting as one'
                  )}
                  {field(
                    'Admin fee',
                    <CurrencyInput value={numberOrNull(form.adminFee) ?? 0} onChange={(v) => set('adminFee', v)} />,
                    'One-off, charged once per booking'
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {isEdit && (
              <TabsContent value="history" className="space-y-4">
                <Card>
                  <CardHeader className="flex-row items-center justify-between pb-3">
                    <CardTitle className="text-sm">Service &amp; compliance log</CardTitle>
                    {totalRunningCost > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {formatCurrency(totalRunningCost)} recorded
                      </span>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {events.length === 0 ? (
                      <p className="py-4 text-center text-sm text-muted-foreground">Nothing logged yet.</p>
                    ) : (
                      events.map((event) => (
                        <div key={event.$id} className="flex items-start justify-between gap-3 rounded-md border p-2.5 text-sm">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <History className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="font-medium capitalize">{event.type}</span>
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(event.occurredAt), 'd MMM yyyy')}
                              </span>
                            </div>
                            {event.description && <p className="mt-0.5 text-xs text-muted-foreground">{event.description}</p>}
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {[
                                event.odometer ? `${Number(event.odometer).toLocaleString()} km` : null,
                                event.supplier,
                                event.expiresAt ? `valid to ${format(new Date(event.expiresAt), 'd MMM yyyy')}` : null,
                                event.expiryOdometer ? `valid to ${Number(event.expiryOdometer).toLocaleString()} km` : null
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                          </div>
                          {event.cost ? <span className="shrink-0 text-sm tabular-nums">{formatCurrency(event.cost)}</span> : null}
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Bookings ({history.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {history.length === 0 ? (
                      <p className="py-4 text-center text-sm text-muted-foreground">No bookings yet.</p>
                    ) : (
                      history.slice(0, 25).map((booking) => (
                        <div key={booking.$id} className="flex items-center justify-between rounded-md border p-2.5 text-sm">
                          <span className="truncate">
                            {priv.name(
                              typeof booking.customer === 'object' && booking.customer
                                ? `${booking.customer.firstName || ''} ${booking.customer.lastName || ''}`.trim()
                                : booking.title || booking.driverName || 'Booking'
                            )}
                          </span>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {format(new Date(booking.scheduledStart), 'd MMM')} → {format(new Date(booking.scheduledEnd), 'd MMM yyyy')}
                          </span>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </div>
        </Tabs>

        <DialogFooter className="flex-row items-center justify-between gap-2 border-t pt-4 sm:justify-between">
          <div>
            {isEdit && (
              <Button variant="ghost" size="sm" onClick={handleDelete} className="text-destructive hover:text-destructive">
                <Trash2 className="mr-1 h-4 w-4" /> Remove
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add vehicle'}
            </Button>
          </div>
        </DialogFooter>
        <DeleteConfirmDialog
          open={confirmDeleteOpen}
          onOpenChange={setConfirmDeleteOpen}
          onConfirm={confirmDelete}
        />
      </DialogContent>
    </Dialog>
  );
};

export default FleetVehicleForm;
